import {SwUpdate} from '@angular/service-worker';
import {ApplicationRef, Injectable} from '@angular/core';
import {concat, first, interval, Subscription} from 'rxjs';

/** Shared with the boot watchdog in index.html - guards against reload loops. */
const RECOVERY_KEY = 'ot-recovery';

@Injectable({
  providedIn: 'root'
})
export class PwaUpdateService {

  vs$: Subscription | undefined;

  constructor(private appRef: ApplicationRef, private updates: SwUpdate) {
    this.intervalCheckForUpdate();
    this.handleUpdate();
    this.updates.unrecoverable.subscribe(event => {
      console.error('SW in an unrecoverable state:', event.reason);
      this.resetServiceWorker();
    });
  }

  handleUpdate() {
    const enabled = this.updates.isEnabled;

    if (!enabled || this.vs$) {
      // Already wired up - a second call (e.g. from a component) must not add another subscription,
      // otherwise every version event fires its handler twice.
      return;
    }

    this.updates.checkForUpdate().then(r => console.log('App Updated', r)).catch(console.error);

    this.vs$ = this.updates.versionUpdates.subscribe(e => {
      switch (e.type) {
        case 'VERSION_DETECTED':
          console.log(`Downloading new app version: ${e.version.hash}`, e.type);
          break;
        case 'VERSION_READY':
          console.log(`Current app version: ${e.currentVersion.hash}`, e.type);
          console.log(`New app version ready for use: ${e.latestVersion.hash}`, e.type);
          document.location.reload();
          break;
        case 'VERSION_INSTALLATION_FAILED':
          console.log(`Failed to install app version '${e.version.hash}': ${e.error}`, e.type);
          // The cached version can no longer be completed (its files are gone from the server), so
          // drop the worker entirely instead of leaving a broken one registered.
          this.resetServiceWorker();
          break;
      }
    });
  }

  /**
   * Clears the ngsw caches, unregisters the worker and reloads once. The reload is guarded by
   * RECOVERY_KEY (also used by the index.html watchdog) so a persistent failure cannot loop.
   */
  private async resetServiceWorker() {
    let alreadyTried = false;
    try {
      alreadyTried = sessionStorage.getItem(RECOVERY_KEY) !== null;
      sessionStorage.setItem(RECOVERY_KEY, '1');
    } catch (e) {
      console.error(e);
    }

    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.filter(name => /^ngsw:/.test(name)).map(name => caches.delete(name)));
      const registrations = await navigator.serviceWorker?.getRegistrations() ?? [];
      await Promise.all(registrations.map(registration => registration.unregister()));
    } catch (e) {
      console.error('Failed to reset the service worker:', e);
    }

    if (alreadyTried) {
      console.error('Service worker reset already attempted this session - not reloading again.');
      return;
    }
    try {
      document.location.reload();
    } catch (e) {
      console.error(e);
    }
  }

  private intervalCheckForUpdate() {
    const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable === true));
    const everySixHours$ = interval(6 * 60 * 60 * 1000);
    const everySixHoursOnceAppIsStable$ = concat(appIsStable$, everySixHours$);

    everySixHoursOnceAppIsStable$.subscribe(async () => {
      try {
        if (this.updates.isEnabled) {
          const updateFound = await this.updates.checkForUpdate();
          console.log(updateFound ? 'A new version is available.' : 'Already on the latest version.');
        } else {
          console.log('SW Not Enabled');
        }
      } catch (err) {
        console.error('Failed to check for updates:', err);
      }
    });
  }
}
